import {useEffect, useState} from 'react'
import {Mail, Search, Check} from 'lucide-react'
import type {User, UserRole} from '../../types'
import {useApp} from '../../context/AppContext'
import {
  getRoleConfig,
  getStatusConfig,
  getPriorityConfig,
  formatDate,
  isTaskOverdue,
} from '../../utils/helpers'
import {updateUserRole} from '../../services/api'
import Modal from '../common/Modal'
import Avatar from '../common/Avatar'
import Badge from '../common/Badge'

interface UserDetailModalProps {
  user: User
  onClose: () => void
}
const roles: UserRole[] = [
  'manager',
  'coordinator',
  'data-analyst',
  'data-engineer',
  'data-scientist',
  'trainee',
]

export default function UserDetailModal({user, onClose}: UserDetailModalProps) {
  const {state, dispatch} = useApp()
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [selectedRole, setSelectedRole] = useState<UserRole>(user.role)
  const [search, setSearch] = useState('')
  const [taskFilter, setTaskFilter] = useState('pending')
  useEffect(() => {
    setSelectedRole(user.role)
  }, [user.id, user.role])
  const userTeams = state.teams.filter(
    (team) => team.memberIds.includes(user.id) || team.leadId === user.id
  )
  const userTasks = state.tasks.filter((task) => task.assigneeIds.includes(user.id))
  const pending = userTasks.filter((task) => task.status !== 'done')
  const query = search.trim().toLocaleLowerCase('pt-BR')
  const tasks = userTasks
    .filter(
      (task) =>
        task.title.toLocaleLowerCase('pt-BR').includes(query) &&
        (taskFilter === 'all' ||
          (taskFilter === 'overdue'
            ? isTaskOverdue(task)
            : taskFilter === 'done'
            ? task.status === 'done'
            : task.status !== 'done'))
    )
    .sort((a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999'))

  const handleSaveRole = async () => {
    if (selectedRole === user.role || isUpdating) return
    setIsUpdating(true)
    setError('')
    setSaved(false)
    try {
      await updateUserRole(user.id, selectedRole)
      dispatch({type: 'UPDATE_USER', payload: {...user, role: selectedRole}})
      setSaved(true)
    } catch {
      setError('Não foi possível atualizar o cargo. Tente novamente.')
    } finally {
      setIsUpdating(false)
    }
  }
  const roleConfig = getRoleConfig(user.role)

  return (
    <Modal isOpen closeDisabled={isUpdating} onClose={onClose} title='Perfil da pessoa' size='xl'>
      <div className='p-5 md:p-6 space-y-5'>
        <div className='flex items-start gap-4'>
          <Avatar name={user.name} color={user.avatarColor} size='xl' isOnline={user.isOnline} />
          <div className='flex-1 min-w-0'>
            <h3 className='text-lg font-semibold text-slate-800'>{user.name}</h3>
            <a
              href={'mailto:' + user.email}
              className='inline-flex items-center gap-1.5 text-sm text-slate-500 break-all mt-1'
            >
              <Mail size={14} />
              {user.email}
            </a>
            <div className='mt-2'>
              <Badge className={roleConfig?.color}>
                {roleConfig?.label || 'Cargo não definido'}
              </Badge>
            </div>
          </div>
        </div>
        <div className='grid grid-cols-3 gap-3'>
          {[
            ['Pendentes', pending.length],
            ['Concluídas', userTasks.length - pending.length],
            ['Atrasadas', userTasks.filter((task) => isTaskOverdue(task)).length],
          ].map(([label, value]) => (
            <div key={label} className='p-3 bg-slate-50 rounded-xl'>
              <p className='text-xl font-semibold text-slate-800'>{value}</p>
              <p className='text-xs text-slate-500'>{label}</p>
            </div>
          ))}
        </div>
        <div className='p-4 rounded-xl border border-slate-200'>
          <label
            htmlFor='devops-user-role'
            className='block text-sm font-semibold text-slate-700 mb-2'
          >
            Cargo no DevOps
          </label>
          <div className='flex flex-wrap gap-2'>
            <select
              id='devops-user-role'
              value={selectedRole}
              onChange={(event) => {
                setSelectedRole(event.target.value as UserRole)
                setSaved(false)
              }}
              disabled={isUpdating}
              className='flex-1 min-w-0 px-3 py-2 rounded-xl border border-slate-200 text-sm'
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {getRoleConfig(role).label}
                </option>
              ))}
            </select>
            <button
              type='button'
              onClick={handleSaveRole}
              disabled={isUpdating || selectedRole === user.role}
              className='devops-primary-button disabled:opacity-50'
            >
              {isUpdating ? 'Salvando...' : 'Salvar cargo'}
            </button>
          </div>
          {error && (
            <p role='alert' className='mt-2 text-xs text-red-600'>
              {error}
            </p>
          )}
          {saved && (
            <p role='status' className='mt-2 flex items-center gap-1 text-xs text-emerald-600'>
              <Check size={14} />
              Cargo atualizado.
            </p>
          )}
        </div>
        <div>
          <h4 className='text-sm font-semibold text-slate-700 mb-2'>Equipes</h4>
          <div className='flex flex-wrap gap-1.5'>
            {userTeams.map((team) => (
              <span
                key={team.id}
                className='inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border'
                style={{
                  borderColor: team.color + '40',
                  backgroundColor: team.color + '10',
                  color: team.color,
                }}
              >
                {team.name}
                {team.leadId === user.id ? ' · Liderança' : ''}
              </span>
            ))}
            {!userTeams.length && (
              <span className='text-sm text-slate-400'>Nenhuma equipe vinculada</span>
            )}
          </div>
        </div>
        <div className='space-y-3'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <h4 className='text-sm font-semibold text-slate-700'>
              Tarefas atribuídas ({tasks.length})
            </h4>
            <select
              aria-label='Filtrar tarefas da pessoa'
              value={taskFilter}
              onChange={(event) => setTaskFilter(event.target.value)}
              className='px-3 py-2 rounded-xl border border-slate-200 text-sm'
            >
              <option value='pending'>Pendentes</option>
              <option value='all'>Todas as tarefas</option>
              <option value='overdue'>Atrasadas</option>
              <option value='done'>Concluídas</option>
            </select>
          </div>
          <div className='flex items-center gap-2'>
            <Search size={16} className='text-slate-400' />
            <input
              aria-label='Buscar tarefas da pessoa'
              placeholder='Buscar tarefa...'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className='w-full px-3 py-2 rounded-xl border border-slate-200 text-sm'
            />
          </div>
          {tasks.length ? (
            <div className='devops-table-shell overflow-x-auto'>
              <table className='w-full min-w-[500px]'>
                <thead>
                  <tr className='border-b border-slate-100'>
                    {['Tarefa / quadro', 'Status', 'Prioridade', 'Prazo'].map((heading) => (
                      <th
                        key={heading}
                        className='text-left py-3 px-3 text-xs font-semibold text-slate-500'
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => {
                    const statusCfg = getStatusConfig(task.status),
                      priorityCfg = getPriorityConfig(task.priority)
                    return (
                      <tr key={task.id} className='border-b border-slate-50 hover:bg-slate-50'>
                        <td className='py-3 px-3'>
                          <button
                            onClick={() => {
                              dispatch({type: 'SELECT_TASK', payload: task.id})
                              onClose()
                            }}
                            className='text-sm font-medium text-slate-700 hover:text-brand-600 text-left'
                          >
                            {task.title}
                          </button>
                          <p className='text-xs text-slate-400 mt-1'>
                            {state.boards.find((board) => board.id === task.boardId)?.name ||
                              'Quadro indisponível'}
                          </p>
                        </td>
                        <td className='py-3 px-3'>
                          <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                        </td>
                        <td className='py-3 px-3'>
                          <Badge className={priorityCfg.color} dot={priorityCfg.dot}>
                            {priorityCfg.label}
                          </Badge>
                        </td>
                        <td
                          className={
                            'py-3 px-3 text-sm ' +
                            (isTaskOverdue(task) ? 'text-red-600' : 'text-slate-500')
                          }
                        >
                          {task.dueDate ? formatDate(task.dueDate) : 'Sem prazo'}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='devops-empty-state py-6'>
              <p>
                {userTasks.length
                  ? 'Nenhuma tarefa corresponde aos filtros selecionados.'
                  : 'Nenhuma tarefa atribuída a esta pessoa.'}
              </p>
              {userTasks.length > 0 && (
                <button
                  className='devops-secondary-button'
                  onClick={() => {
                    setSearch('')
                    setTaskFilter('all')
                  }}
                >
                  Mostrar todas
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
